<template>
  <div class="register">
    <h1>Join us!</h1>
    <form @submit.prevent="register">
      <div class="form-group mb-3">
        <label for="activation_code" class="form-label">Activation code</label>
        <input
            v-model="activation_code"
            id="activation_code"
            type="text"
            class="form-control"
            required
            ref="activationCode"
            autocomplete="activation_code"
            aria-describedby="activationCodeError activationCodeSuccess">
        <p v-if="activationCodeError" id="activationCodeError" class="text-danger">{{ activationCodeError }}</p>
        <p v-if="activationCodeSuccess" id="activationCodeSuccess" class="text-success">{{ activationCodeSuccess }}</p>
      </div>
      <div class="form-group mb-3">
        <label for="first_name" class="form-label">First name</label>
        <input v-model="first_name" id="first_name" type="text" class="form-control" autocomplete="first_name" required>
      </div>
      <div class="form-group mb-3">
        <label for="last_name" class="form-label">Last name</label>
        <input v-model="last_name" id="last_name" type="text" class="form-control" autocomplete="last_name" required>
      </div>
      <div class="form-group mb-3">
        <label for="email" class="form-label">Email</label>
        <input
            v-model="email"
            id="email"
            type="email"
            required
            class="form-control"
            autocomplete="email"
            aria-describedby="emailError">
        <p v-if="emailError" id="emailError" class="text-danger">{{ emailError }}</p>
      </div>
      <div class="form-group mb-3">
        <label for="mobile_number" class="form-label">Mobile number</label>
        <input
            v-model="mobile_number"
            id="mobile_number"
            type="tel"
            class="form-control"
            required
            autocomplete="mobile_number"
            aria-describedby="mobileNumberError">
        <p v-if="mobileNumberError" id="mobileNumberError" class="text-danger">{{ mobileNumberError }}</p>
        <p v-if="mobileNumberSuccess" id="mobileNumberSuccess" class="text-success">{{ mobileNumberSuccess }}</p>
      </div>
      <div class="form-group mb-3">
        <label for="username" class="form-label">Username
          <span class="badge bg-warning text-dark">Don't forget this - you'll need it to get in!</span>
        </label>
        <input
            v-model="username"
            id="username"
            type="text"
            class="form-control"
            required
            autocomplete="username"
            aria-describedby="usernameError">
        <p v-if="usernameError" id="usernameError" class="text-danger">{{ usernameError }}</p>
      </div>
      <div class="form-group mb-3 position-relative">
        <label for="password" class="form-label">Password
          <span class="badge bg-warning text-dark">Store this securely - you will need it later on!</span>
        </label>
        <input
            v-model="password"
            id="password"
            :type="showPassword ? 'text': 'password'"
            class="form-control"
            required
            @input="validatePassword"
            autocomplete="new-password"
            aria-describedby="passwordError">
        <i :class="showPassword ? 'fa-regular fa-eye-slash' : 'fa-regular fa-eye'"
           class="position-absolute"
           style="right: 10px; top: 70%; transform: translateY(-50%);"
           @click="togglePasswordVisibility"></i>
      </div>
      <p v-if="passwordError" id="passwordError" class="text-danger">{{ passwordError }}</p>
      <button
          type="submit"
          class="btn btn-primary mb-3"
          :disabled="!activationCodeValid || !usernameValid || !passwordValid || !emailValid || !mobileNumberValid">
        Register
      </button>
    </form>
    <p v-if="error" class="text-danger">{{ error }}</p>
    <p v-if="success" class="text-success">{{ success }}</p>
  </div>
</template>

<script>
import {getCSRFToken} from '@/store/auth'
import {ref, watch} from 'vue'
import {useAuthStore} from '@/store/auth'
import {API_BASE_URL} from "@/config";

function debounce(fn, delay) {
  let timeoutID;
  return function (...args) {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => fn.apply(this, args), delay);
  };
}

async function callValidateActivationCodeAPI(newCode) {
  try {
    const response = await fetch(`${API_BASE_URL}/validate-activation-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCSRFToken()
      },
      body: JSON.stringify({
        activation_code: newCode
      }),
      credentials: 'include'
    })
    const data = await response.json()
    return {status: data.status, message: data.message}
  } catch (err) {
    console.error('Error validating activation code', err)
    return {ok: false, message: 'An error occurred. Please try again.'}
  }
}

async function callCheckUsernameAPI(newUsername) {
  try {
    const response = await fetch(
        `${API_BASE_URL}/username-is-available`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken()
          },
          body: JSON.stringify({
            username: newUsername
          }),
          credentials: 'include'
        })
    const data = await response.json()
    return {status: data.status}
  } catch (err) {
    console.error('Error checking username', err)
    return {ok: false, message: 'An error occurred. Please try again.'}
  }
}

async function callValidatePasswordAPI(password) {
  try {
    const response = await fetch(`${API_BASE_URL}/validate-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCSRFToken()
      },
      body: JSON.stringify({password}),
      credentials: 'include'
    })
    const data = await response.json()
    return data
  } catch (err) {
    console.error('Error validating password', err)
    return {status: 'invalid', errors: ['An error occurred. Please try again.']}
  }
}

async function callValidateEmailAPI(email) {
  try {
    const response = await fetch(`${API_BASE_URL}/validate-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCSRFToken()
      },
      body: JSON.stringify({email}),
      credentials: 'include'
    })
    const data = await response.json()
    return data
  } catch (err) {
    console.error('Error validating email', err)
    return {status: 'invalid', errors: ['An error occurred. Please try again.']}
  }
}

async function callValidateMobileNumberAPI(mobileNumber) {
  try {
    const response = await fetch(`${API_BASE_URL}/validate-mobile-number`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCSRFToken()
      },
      body: JSON.stringify({mobile_number: mobileNumber}),
      credentials: 'include'
    });
    const data = await response.json();
    return data;
  } catch (err) {
    console.error('Error validating mobile number', err);
    return {status: 'invalid', errors: ['An error occurred. Please try again.']};
  }
}


export default {
  mounted() {
    this.$refs.activationCode.focus();
  },
  setup() {
    const authStore = useAuthStore()
    const activation_code = ref('')
    const activationCodeError = ref('')
    const activationCodeSuccess = ref('')
    const activationCodeValid = ref(false)
    const username = ref('')
    const usernameError = ref('')
    const usernameAvailable = ref(true)
    const usernameValid = ref(false)
    const first_name = ref('')
    const last_name = ref('')
    const email = ref('')
    const emailError = ref('')
    const emailValid = ref(false)
    const mobile_number = ref('')
    const mobileNumberError = ref('')
    const mobileNumberSuccess = ref('')
    const mobileNumberValid = ref(false)
    const password = ref('')
    const passwordError = ref('')
    const passwordValid = ref(false)
    const error = ref('')
    const success = ref('')
    const showPassword = ref(false)

    const validateActivationCode = debounce(async (newCode) => {
      if (newCode) {
        const result = await callValidateActivationCodeAPI(newCode)
        if (result.status !== "valid") {
          // Bad activation code
          activationCodeSuccess.value = ''
          activationCodeError.value = result.message
          activationCodeValid.value = false
        } else {
          // Good activation code
          activationCodeSuccess.value = result.message
          activationCodeError.value = ''
          activationCodeValid.value = true
        }
      } else {
        activationCodeError.value = ''
        activationCodeValid.value = false
      }
    }, 1000)

    const validateUsername = debounce(async (newUsername) => {
      if (newUsername) {
        let result = await callCheckUsernameAPI(newUsername)
        let counter = 1;
        while (result.status !== "free") {
          const newUsernameWithNumber = `${newUsername}${counter}`;
          result = await callCheckUsernameAPI(newUsernameWithNumber);
          counter++;
        }
        if (counter > 1) {
          username.value = `${newUsername}${counter - 1}`;
        }
        usernameError.value = '';
        usernameValid.value = true;
      }
    }, 1000)

    const validateEmail = debounce(async (newEmail) => {
      if (newEmail) {
        const result = await callValidateEmailAPI(newEmail)
        if (result.status === 'valid') {
          emailError.value = ''
          emailValid.value = true
        } else {
          emailError.value = result.errors.join(', ')
          emailValid.value = false
        }
      } else {
        emailError.value = ''
        emailValid.value = false
      }
    }, 500)

    watch(email, (newEmail) => {
      validateEmail(newEmail)
    })

    const validateMobileNumber = debounce(async (newMobileNumber) => {
      if (newMobileNumber) {
        const result = await callValidateMobileNumberAPI(newMobileNumber)
        if (result.status === 'valid') {
          // Good mobile number
          mobileNumberSuccess.value = result.success
          mobileNumberError.value = ''
          mobileNumberValid.value = true
        } else {
          // Bad mobile number
          mobileNumberSuccess.value = ''
          mobileNumberError.value = result.errors.join(', ')
          mobileNumberValid.value = false
        }
      } else {
        mobileNumberError.value = ''
        mobileNumberValid.value = false
      }
    }, 500);
    watch(mobile_number, (newMobileNumber) => {
      validateMobileNumber(newMobileNumber);
    })


    const validatePassword = debounce(async (newPassword) => {
      if (newPassword) {
        const result = await callValidatePasswordAPI(newPassword)
        if (result.status === 'valid') {
          passwordError.value = ''
          passwordValid.value = true
        } else {
          passwordError.value = result.errors.join(', ')
          passwordValid.value = false
        }
      } else {
        passwordError.value = ''
        passwordValid.value = false
      }
    }, 500)

    watch(activation_code, (newCode) => {
      validateActivationCode(newCode)
    })

    // Convert username to lowercase and remove special characters
    // Also ensure username starts with a letter
    watch(username, (newUserName) => {
      let formattedUsername = newUserName.toLowerCase().replace(/[^a-z0-9+-_@]/g, '')
      if (formattedUsername && !/^[a-z]/.test(formattedUsername)) {
        formattedUsername = formattedUsername.replace(/^[^a-z]+/, '')
      }
      username.value = formattedUsername
      validateUsername(formattedUsername)
    })

    watch(password, (newPassword) => {
      validatePassword(newPassword)
    })

    // Watch first_name and last_name to dynamically update username and capitalize names
    watch([first_name, last_name], ([newFirstName, newLastName]) => {
      // Remove leading and trailing spaces from first and last name
      const trimmedFirstName = String(newFirstName).trim();
      const trimmedLastName = String(newLastName).trim();

      // Split the firstName on spaces or on dash (-) sign.
      // If the firstName is split into multiple parts,
      // then take the first 2 letters of each part.
      // If the firstName is only split into one part,
      // take the first 3 letters of the firstName.
      const firstNameParts = trimmedFirstName.split(/[\s-]+/);
      const formattedFirstName = firstNameParts.length > 1
          ? firstNameParts.map(part => part.slice(0, 1)).join('')
          : trimmedFirstName.slice(0, 3);

      // Split the lastName on spaces.
      // If the lastName is split into multiple parts,
      // then take the first 2 letters of each part.
      // If the lastName is only split into one part,
      // take the first 3 letters of the lastName.
      const lastNameParts = trimmedLastName.split(' ');
      const formattedLastName = lastNameParts.length > 1
          ? lastNameParts.map(part => part.slice(0, 2)).join('')
          : trimmedLastName.slice(0, 3);

      username.value = (formattedFirstName + formattedLastName).toLowerCase();
    });

    function togglePasswordVisibility() {
      showPassword.value = !showPassword.value;
    }

    return {
      authStore,
      activationCodeError,
      activationCodeSuccess,
      activationCodeValid,
      error,
      success,
      activation_code,
      username,
      usernameError,
      usernameValid,
      usernameAvailable,
      first_name,
      last_name,
      email,
      emailError,
      emailValid,
      mobile_number,
      mobileNumberError,
      mobileNumberSuccess,
      mobileNumberValid,
      password,
      passwordError,
      passwordValid,
      showPassword,
      togglePasswordVisibility,
    }
  },


  methods: {
    async register() {
      try {
        const response = await fetch(`${API_BASE_URL}/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken()
          },
          body: JSON.stringify({
            activation_code: this.activation_code,
            username: this.username,
            first_name: this.first_name,
            last_name: this.last_name,
            email: this.email,
            mobile_number: this.mobile_number,
            password: this.password
          }),
          credentials: 'include'
        })
        const data = await response.json()
        if (response.ok) {
          this.success = 'Your registration was successful.<br>' +
              'Please check your email to activate your new account.<br>' +
              'The activation link is valid for 1 hour only.'
          this.$router.push({path: '/', query: {success: this.success}})
        } else {
          this.error = data.error || 'Registration failed'
        }
      } catch (err) {
        this.error = 'An error occurred during registration: ' + err
      }
    }
  }
}
</script>
